import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Cognitive/Psychosocial Development subsection adapter
 * Handles communication, cognitive assessment, and substance abuse history (5 fields)
 */
export class CognitivePsychosocialAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Cognitive/Psychosocial assessment fields
    const communicationAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT
    )

    const cognitiveAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT
    )

    const historySubstanceAbuse = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE
    )?.at(0)

    const substanceAbuseNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [DischargeFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT]: communicationAssessment,
      [DischargeFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT]: cognitiveAssessment,
      [DischargeFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE]: historySubstanceAbuse,
      [DischargeFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES]: substanceAbuseNotes,
      [DischargeFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (
      this.formDataFieldExists(DischargeFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT)
    ) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT,
        value: this.getFormDataField(
          DischargeFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT,
        value: this.getFormDataField(DischargeFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT)
      })
    }
    if (
      this.formDataFieldExists(DischargeFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE)
    ) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE,
        value: this.getFormDataField(
          DischargeFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES,
        value: this.getFormDataField(
          DischargeFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY,
        value: this.getFormDataField(DischargeFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
